import React, { useState, useMemo } from 'react'
import { useWindowSize } from '../../../../hooks/useWindowSize'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { IonButton, IonCol, IonGrid, IonItem, IonLabel, IonListHeader, IonRow, IonText } from '@ionic/react'
import { getPulsaraSelector } from '../../selectors'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IPatientConflictField } from '../../store/pulsara.types'
import { getPatientConflicts, buildResolvedPatient } from '../../utils/pulsaraConflict.util'
import { setPatientSelections, setImportSelections } from '../../store/pulsara.actions'
import CCSelectionPopup from './SelectionPopup.component'
import './PatientConflict.component.css'
import { showToast } from '../../../../components/toast/store/toast.actions'
import { getToastSelector } from '../../../../components/toast/selectors'
import { ToastColor } from '../../../../components/toast/store/toast.constants'

const PatientConflict: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { pulsaraDetails, emsChartsData, patientSelections, importSelections } = useSelector(getPulsaraSelector)
  const { FORCE_PT_SEARCH } = useSelector(getServiceConfigSelector)
  const toastState = useSelector(getToastSelector)
  const [showCCPopup, setShowCCPopup] = useState<boolean>(false)
  const { width: windowWidth } = useWindowSize()

  const conflictFields = useMemo(
    () => getPatientConflicts(pulsaraDetails, emsChartsData),
    [emsChartsData, pulsaraDetails]
  )

  const complaintField = conflictFields.find((f) => f.key === 'complaint') ?? null

  const updateSelection = (key: string, source: 'pulsara' | 'emsCharts') => {
    let newSelections: Record<string, 'pulsara' | 'emsCharts' | null>

    if (FORCE_PT_SEARCH === 0 && patientSelections[key] === source) {
      // Checkbox behavior
      newSelections = { ...patientSelections, [key]: null }
    } else {
      // Radio behavior
      newSelections = { ...patientSelections, [key]: source }
    }

    const resolvedPatient = buildResolvedPatient(pulsaraDetails, emsChartsData, newSelections)

    dispatch(setPatientSelections(newSelections))
    dispatch(
      setImportSelections({
        ...importSelections,
        patient: {
          ...importSelections.patient,
          patientDemographics: {
            ...importSelections.patient?.patientDemographics,
            ...resolvedPatient.patientDemographics,
          },
        },
        situation: {
          ...(importSelections.situation ?? {}),
          ...resolvedPatient.situation,
        },
      })
    )
  }

  const shouldShowCCPopup = (field: IPatientConflictField): boolean => {
    if (!field || field.key !== 'complaint') return false
    const numberOfCharacters = windowWidth >= 768 ? 21 : 15
    return field.emsChartsValue.length > numberOfCharacters || field.pulsaraValue.length > numberOfCharacters
  }

  const handleSourceSelect = (field: IPatientConflictField, source: 'pulsara' | 'emsCharts', e: React.MouseEvent) => {
    e.preventDefault()

    if (field.key === 'complaint' && shouldShowCCPopup(field)) {
      setShowCCPopup(true)
    } else {
      if ((field.key === 'firstName' || field.key === 'lastName') && !!emsChartsData.ptid && !toastState.showToast) {
        dispatch(
          showToast({
            message: t('pulsara.patientConflict.firstLastNameChangeWarning'),
            duration: 2500,
            position: 'top',
            color: ToastColor.Warning,
          })
        )
      }
      updateSelection(field.key, source)
    }
  }

  const handleCCPopupDone = (source: 'pulsara' | 'emsCharts' | null) => {
    const newSelections = { ...patientSelections, complaint: source }

    const resolvedPatient = buildResolvedPatient(pulsaraDetails, emsChartsData, newSelections)

    dispatch(setPatientSelections(newSelections))
    dispatch(
      setImportSelections({
        ...importSelections,
        patient: {
          ...importSelections.patient,
          patientDemographics: {
            ...importSelections.patient?.patientDemographics,
            ...resolvedPatient.patientDemographics,
          },
        },
        situation: {
          ...(importSelections.situation ?? {}),
          ...resolvedPatient.situation,
        },
      })
    )
    setShowCCPopup(false)
  }

  const handleCCPopupCancel = () => {
    setShowCCPopup(false)
  }

  const renderFieldValue = (field: IPatientConflictField, source: 'pulsara' | 'emsCharts', value: string) => {
    if (!value) {
      return <IonLabel>{t('pulsara.NotAvailable')}</IonLabel>
    }

    if (field.hasConflict) {
      const isSelected = patientSelections[field.key] === source
      return (
        <IonButton
          fill={isSelected ? 'solid' : 'outline'}
          color={isSelected ? 'primary' : ''}
          size='small'
          className='patient-conflict-button'
          onClick={(e) => handleSourceSelect(field, source, e)}>
          <IonLabel>{value}</IonLabel>
        </IonButton>
      )
    }
    return <IonLabel className='no-conflict-label'>{value}</IonLabel>
  }

  return (
    <>
      <IonItem className='patient-conflict-warnings sc-danger-subtle'>
        <IonLabel className='ion-text-wrap'>{t('pulsara.patientConflict.warning')}</IonLabel>
      </IonItem>

      <IonListHeader className='patient-conflict-header'>
        <IonGrid>
          <IonRow>
            <IonCol className='pt-0 pb-0'>
              <IonLabel>{t('pulsara.patientConflict.field')}</IonLabel>
            </IonCol>
            <IonCol className='pt-0 pb-0'>
              <IonLabel>{t('pulsara.patientConflict.emsCharts')}</IonLabel>
            </IonCol>
            <IonCol className='pt-0 pb-0'>
              <IonLabel>{t('pulsara.patientConflict.pulsara')}</IonLabel>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonListHeader>

      <hr className='field-divider' />

      {conflictFields.map((field: IPatientConflictField) => (
        <IonItem key={field.key} className='patient-conflict-item' lines='none'>
          <IonGrid className={`ion-no-padding w-100 ${field.hasConflict ? 'conflict-row' : ''}`}>
            <IonRow className='ion-align-items-center'>
              <IonCol>
                <IonLabel className='fw-600'>
                  {field.label}
                  {field.hasConflict && <IonText> *</IonText>}
                </IonLabel>
              </IonCol>
              <IonCol>
                <IonRow>
                  <IonCol size='6'>{renderFieldValue(field, 'emsCharts', field.emsChartsValue)}</IonCol>
                  <IonCol size='6'>{renderFieldValue(field, 'pulsara', field.pulsaraValue)}</IonCol>
                </IonRow>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonItem>
      ))}

      <IonItem className='patient-conflict-warnings sc-danger-subtle bottom-fixed'>
        <IonLabel className='ion-text-wrap'>{t('pulsara.patientConflict.overwriteWarning')}</IonLabel>
      </IonItem>

      <CCSelectionPopup
        isOpen={showCCPopup}
        field={complaintField}
        selectedSource={patientSelections['complaint']}
        isRequired={FORCE_PT_SEARCH === 1}
        onDone={(source: 'pulsara' | 'emsCharts' | null) => handleCCPopupDone(source)}
        onCancel={handleCCPopupCancel}
      />
    </>
  )
}

export default PatientConflict
